import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, TrendingUp, TrendingDown, DollarSign, FileText, Filter, BarChart3, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { AccrualTable } from './AccrualTable';
import { AccrualFilters } from './AccrualFilters';
import { AccrualChart } from './AccrualChart';
import { BatchActionsDropdown } from '../transactions/BatchActionsDropdown';
import { BatchDatePickerModal } from '../transactions/BatchDatePickerModal';
import { BatchAmountModal } from '../transactions/BatchAmountModal';
import { BatchAccountSelectModal } from '../transactions/BatchAccountSelectModal';
import { TransactionFormContainer } from '../transactions/TransactionFormContainer';
import { 
  AccrualTransaction,
  AccrualSummary,
  AccrualFilters as AccrualFiltersType,
  AccrualGrouped,
  AccrualPeriod,
  AccrualStatus,
  getCurrentPeriod,
  formatCurrency,
  formatPeriod,
  isTransactionInPeriod,
  calculatePaymentRate,
  DEFAULT_CATEGORIES
} from '../../types/accrual';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { useAccrualData } from '@/hooks/useAccrualData';
import { useBatchOperationModals } from '@/hooks/useBatchOperationModals';

export function AccrualPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'table' | 'chart'>('table');
  const [selectedTransactions, setSelectedTransactions] = useState<Set<string>>(new Set());

  // Edit transaction state
  const [editingTransactionId, setEditingTransactionId] = useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Batch operation modals state (using custom hook)
  const { modals, openModal, closeModal } = useBatchOperationModals();

  // Initialize filters
  const [filters, setFilters] = useState<AccrualFiltersType>({
    period: getCurrentPeriod(),
    status: Object.values(AccrualStatus),
    type: ['receita', 'despesa'],
    sort_by: 'competence_date',
    sort_direction: 'desc'
  });

  // Fetch real data from Supabase
  const {
    data: accrualGroupedData,
    transactions: accrualData,
    isLoading,
    isError,
    error,
    refetch
  } = useAccrualData({
    period: filters.period || getCurrentPeriod(),
    filters
  });

  // Use data from hook (already filtered and grouped)
  const groupedData = useMemo((): AccrualGrouped[] => {
    if (!accrualGroupedData) return [];
    return [accrualGroupedData];
  }, [accrualGroupedData]);

  const currentGroup = groupedData[0];

  // Selection handlers (optimized with useCallback)
  const handleSelectTransaction = useCallback((transactionId: string, selected: boolean) => {
    setSelectedTransactions(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(transactionId);
      } else {
        newSet.delete(transactionId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback((selected: boolean) => {
    if (selected && currentGroup) {
      const allTransactionIds = currentGroup.transactions.map(t => t.id);
      setSelectedTransactions(new Set(allTransactionIds));
    } else {
      setSelectedTransactions(new Set());
    }
  }, [currentGroup]);

  // Handle transaction click for editing (optimized with useCallback)
  const handleTransactionClick = useCallback((transactionId: string) => {
    setEditingTransactionId(transactionId);
    setIsEditDialogOpen(true);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Competência</h1>
        <p className="text-muted-foreground">
          Gerencie lançamentos por período de competência fiscal
        </p>
      </div>

      {/* Filters */}
      <AccrualFilters
        filters={filters}
        onFiltersChange={setFilters}
        loading={isLoading}
        onRefresh={() => window.location.reload()}
      />

      {/* Summary Cards */}
      {currentGroup && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Receitas</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(currentGroup.summary.total_revenue)}
              </div>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(currentGroup.summary.paid_revenue)} realizadas
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Despesas</CardTitle>
              <TrendingDown className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {formatCurrency(currentGroup.summary.total_expenses)}
              </div>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(currentGroup.summary.paid_expenses)} pagas
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Resultado</CardTitle>
              <DollarSign className={`h-4 w-4 ${currentGroup.summary.net_result >= 0 ? 'text-green-600' : 'text-red-600'}`} />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${currentGroup.summary.net_result >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(currentGroup.summary.net_result)}
              </div>
              <p className="text-xs text-muted-foreground">
                {currentGroup.summary.net_result >= 0 ? 'Lucro' : 'Prejuízo'} no período
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Taxa de Pagamento</CardTitle>
              <FileText className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {currentGroup.summary.payment_rate.toFixed(1)}%
              </div>
              <p className="text-xs text-muted-foreground">
                {currentGroup.summary.transactions_count} lançamentos
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Status Overview */}
      {currentGroup && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Resumo por Status</CardTitle>
            <CardDescription>
              Distribuição dos lançamentos de {formatPeriod(currentGroup.period)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(currentGroup.by_status).map(([status, data]) => (
                <div key={status} className="flex items-center space-x-2">
                  <Badge variant="outline" className="flex items-center gap-1">
                    <span className={`w-2 h-2 rounded-full ${
                      status === AccrualStatus.PAID ? 'bg-green-500' :
                      status === AccrualStatus.PENDING ? 'bg-yellow-500' :
                      status === AccrualStatus.OVERDUE ? 'bg-red-500' :
                      status === AccrualStatus.PARTIALLY_PAID ? 'bg-blue-500' :
                      'bg-muted0'
                    }`} />
                    {status === AccrualStatus.PAID ? 'Pago' :
                     status === AccrualStatus.PENDING ? 'Pendente' :
                     status === AccrualStatus.OVERDUE ? 'Em Atraso' :
                     status === AccrualStatus.PARTIALLY_PAID ? 'Parcial' :
                     'Cancelado'}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {data.count} ({formatCurrency(data.amount)})
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Batch Actions */}
      {selectedTransactions.size > 0 && (
        <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-sm text-blue-800">
              {selectedTransactions.size} lançamento(s) selecionado(s)
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedTransactions(new Set())}
              className="text-blue-600 border-blue-300 hover:bg-blue-100"
            >
              Limpar seleção
            </Button>
            <BatchActionsDropdown
              selectedTransactions={selectedTransactions}
              onClearSelection={() => setSelectedTransactions(new Set())}
              onOpenDateModal={() => openModal('date')}
              onOpenAmountModal={() => openModal('amount')}
              onOpenAccountModal={() => openModal('account')}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'table' | 'chart')}>
        <TabsList>
          <TabsTrigger value="table" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Tabela
          </TabsTrigger>
          <TabsTrigger value="chart" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Gráfico
          </TabsTrigger>
        </TabsList>

        <TabsContent value="table" className="space-y-4">
          {currentGroup && (
            <AccrualTable
              data={currentGroup.transactions}
              onSelectTransaction={handleSelectTransaction}
              onSelectAll={handleSelectAll}
              selectedTransactions={selectedTransactions}
              loading={isLoading}
              sortable={true}
              onTransactionClick={handleTransactionClick}
            />
          )}
        </TabsContent>

        <TabsContent value="chart" className="space-y-4">
          {currentGroup && (
            <AccrualChart
              data={currentGroup.transactions}
              summary={currentGroup.summary}
              categoryData={currentGroup.by_category}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Error State */}
      {isError && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold mb-2 text-red-900">Erro ao carregar dados</h3>
            <p className="text-sm text-red-600 text-center max-w-sm mb-4">
              {error?.message || 'Falha ao carregar dados de competência'}
            </p>
            <Button
              onClick={() => refetch()}
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && !isError && (!currentGroup || currentGroup.transactions.length === 0) && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Calendar className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Nenhum lançamento encontrado</h3>
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              Não há lançamentos para o período selecionado ou que atendam aos filtros aplicados.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Batch Operation Modals */}
      <BatchDatePickerModal
        open={modals.date}
        onOpenChange={(open) => open ? openModal('date') : closeModal('date')}
        selectedTransactions={selectedTransactions}
        onClearSelection={() => setSelectedTransactions(new Set())}
      />
      <BatchAmountModal
        open={modals.amount}
        onOpenChange={(open) => open ? openModal('amount') : closeModal('amount')}
        selectedTransactions={selectedTransactions}
        onClearSelection={() => setSelectedTransactions(new Set())}
      />
      <BatchAccountSelectModal
        open={modals.account}
        onOpenChange={(open) => open ? openModal('account') : closeModal('account')}
        selectedTransactions={selectedTransactions}
        onClearSelection={() => setSelectedTransactions(new Set())}
      />

      {/* Transaction Edit Modal */}
      <TransactionFormContainer
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        transactionId={editingTransactionId}
        onClose={() => {
          setIsEditDialogOpen(false);
          setEditingTransactionId(null);
          refetch(); // Refresh data after editing
        }}
      />
    </div>
  );
}